import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { Skeleton } from "components/ui/skeleton";

import useAdminInstitution from "Explore/Hooks/useAdminInstitution";
import useOverviewData from "./Hooks/useOverviewData";
import ComingSoon from "Explore/Components/ComingSoon/ComingSoon";

import { InstitutionHeader, BiographyCard } from "./Components/BasicInfo";
import ResearchStrengthCard from "./Components/ResearchStrengthCard";
import SocialImpactCard from "./Components/SocialImpactCard";
import TopRankingCard from "./Components/TopRankingCard";
import TopFacultyCard from "./Components/TopFacultyCard";
import TopSponsorsCard from "./Components/TopSponsorsCard";
import FeaturedGrantsCard from "./Components/FeaturedGrantsCard";
import SimilarInstitutionsCard from "./Components/SimilarInstitutionsCard";
import CollaborationsCard from "./Components/CollaborationsCard";
import SdgDetailDrawer from "./Components/SdgDetailDrawer";

const OverviewSkeleton = () => (
  <div className="flex flex-col gap-6 p-6 w-full animate-pulse">
    <Skeleton className="h-48 w-full rounded-xl" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
    </div>
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
      <div className="space-y-6">
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  </div>
);

const Overview = () => {
  const navigate = useNavigate();
  const { institution, isLoading: isSummaryLoading, error: summaryError, isAdmin } = useAdminInstitution();
  
  const { 
    data, 
    socialImpactData, 
    isLoading, 
    isLoadingSocialImpact, 
    error, 
    socialImpactError,
    retrySection,
    fetchSocialImpact
  } = useOverviewData(institution?.id);

  const [selectedSdg, setSelectedSdg] = useState(null);

  if (!isAdmin) {
    return (
      <ComingSoon
        title="Overview"
        description="Your personalized dashboard overview is coming soon."
      />
    );
  }

  if (isSummaryLoading) {
    return <OverviewSkeleton />;
  }

  if (summaryError) {
    return (
      <div className="p-6">
        <div className="bg-destructive/15 text-destructive p-4 rounded-lg border border-destructive/20">
          <p className="font-medium">Failed to load overview data</p>
          <p className="text-sm mt-1">{summaryError}</p>
        </div>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
          <Building2 size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No Institution Found</h3>
        <p className="text-muted-foreground">No institution is currently assigned to this account.</p>
      </div>
    );
  }

  // Helper to handle "See more" navigations
  const handleSeeMore = (type, payload) => {
    switch (type) {
      case 'research_strength':
        navigate(`/explore/report-detail#section-research-strength`, {
          state: {
            entityId: institution.id,
            entityType: "institution",
            entity: institution,
            targetSection: "section-research-strength",
            researchTab: payload,
          }
        });
        break;
      case 'faculty':
        navigate(`/explore/report-detail#section-top-faculty`, {
          state: {
            entityId: institution.id,
            entityType: "institution",
            entity: institution,
            targetSection: "section-top-faculty",
            tab: payload,
          }
        });
        break;
      case 'grants':
        navigate(`/explore/report-detail#section-featured-grants`, {
          state: {
            entityId: institution.id,
            entityType: "institution",
            entity: institution,
            targetSection: "section-featured-grants",
          }
        });
        break;
      case 'sponsors':
        navigate(`/explore/report-detail#section-top-sponsors`, {
          state: {
            entityId: institution.id,
            entityType: "institution",
            entity: institution,
            targetSection: "section-top-sponsors",
          }
        });
        break;
      case 'peers':
        navigate(`/explore/peer`, {
          state: {
            peerId: institution.id,
            entityId: institution.id,
            entity: {
              id: institution.id,
              name: institution.name,
              type: "institution",
              avatar: institution.logo_url || (institution.id ? `https://media.academic.online/institutions_images/${institution.id}/seal.png` : null)
            }
          }
        });
        break;
      case 'collaborations':
        navigate(`/explore/collaborations`, {
          state: {
            entityId: institution.id,
            institutionId: institution.id,
            entity: {
              id: institution.id,
              name: institution.name,
              type: "institution",
              avatar: institution.logo_url || (institution.id ? `https://media.academic.online/institutions_images/${institution.id}/seal.png` : null)
            }
          }
        });
        break;
      default:
        break;
    }
  };

  const hiddenFields = data?.hidden_fields || {};
  const errors = data?.errors || {};

  return (
    <div className="flex flex-col gap-6 p-6 w-full h-full animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out fill-mode-both">
      <InstitutionHeader institution={institution} kpis={data?.kpis} isLoading={isLoading} />

      
      {/* About Section */}
      <div id="overview" className="scroll-mt-16">
        <BiographyCard text={institution.biography || institution.description} />
      </div>

      <div className="flex flex-col gap-6">
        {/* Row 1: Research Strength | Top Faculty */}
        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <div id="research" className="scroll-mt-16 h-full flex flex-col">
            <ResearchStrengthCard 
              data={data?.research_strength}
              isLoading={isLoading}
              error={errors['research_strength']}
              isHidden={hiddenFields['research_strength']}
              onRetry={() => retrySection('research_strength')}
              onSeeMore={(tab) => handleSeeMore('research_strength', tab)}
            />
          </div>
          <div id="faculty" className="scroll-mt-16 h-full flex flex-col">
            <TopFacultyCard 
              data={data?.top_faculty}
              isLoading={isLoading}
              error={errors['top_faculty']}
              isHidden={hiddenFields['top_faculty']}
              onRetry={() => retrySection('top_faculty')}
              onSeeMore={(tab) => handleSeeMore('faculty', tab)}
            />
          </div>
        </div>

        {/* Row 2: Funding (Grants | Sponsors) */}
        <div id="funding" className="scroll-mt-16 grid lg:grid-cols-2 gap-6 items-stretch">
          <div className="h-full flex flex-col">
            <FeaturedGrantsCard 
              data={data?.featured_grants}
              isLoading={isLoading}
              error={errors['featured_grants']}
              isHidden={hiddenFields['featured_grants']}
              onRetry={() => retrySection('featured_grants')}
              onSeeMore={() => handleSeeMore('grants')}
            />
          </div>
          <div className="h-full flex flex-col">
            <TopSponsorsCard 
              data={data?.top_sponsors}
              isLoading={isLoading}
              error={errors['top_sponsors']}
              isHidden={hiddenFields['top_sponsors']}
              onRetry={() => retrySection('top_sponsors')}
              onSeeMore={() => handleSeeMore('sponsors')}
            />
          </div>
        </div>

        {/* Row 3: Top Ranking Subjects */}
        <div className="grid grid-cols-1 gap-6 items-stretch">
          <div className="h-full flex flex-col">
            <TopRankingCard 
              data={data?.top_ranking}
              isLoading={isLoading}
              error={errors['top_ranking']}
              isHidden={hiddenFields['top_ranking']}
              onRetry={() => retrySection('top_ranking')}
            />
          </div>
        </div>

        {/* Row 4: SDG */}
        <div className="grid grid-cols-1 gap-6 items-stretch">
          <div id="sdg" className="scroll-mt-16 h-full flex flex-col">
            <SocialImpactCard 
              data={socialImpactData}
              isLoading={isLoadingSocialImpact}
              error={socialImpactError}
              isHidden={hiddenFields['sdg']}
              onRetry={fetchSocialImpact}
              onSdgClick={setSelectedSdg}
            />
          </div>
        </div>

        {/* Row 5: Peer Institutions & Collaborations */}
        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          <div id="similar" className="scroll-mt-16 h-full flex flex-col">
            <SimilarInstitutionsCard 
              data={data?.peers}
              isLoading={isLoading}
              error={errors['peers']}
              isHidden={hiddenFields['peers']}
              onRetry={() => retrySection('peers')}
              onSeeMore={() => handleSeeMore('peers')}
            />
          </div>
          <div id="collaborations" className="scroll-mt-16 h-full flex flex-col">
            <CollaborationsCard 
              data={data?.collaborations}
              isLoading={isLoading}
              error={errors['collaborations']}
              isHidden={hiddenFields['collaborations']}
              onRetry={() => retrySection('collaborations')}
              onSeeMore={() => handleSeeMore('collaborations')}
            />
          </div>
        </div>
      </div>

      <SdgDetailDrawer 
        institutionId={institution?.id}
        sdgNum={selectedSdg}
        isOpen={!!selectedSdg}
        onClose={() => setSelectedSdg(null)}
      />
    </div>
  );
};

export default Overview;
